import React, { useEffect, useRef } from "react";
import * as echarts from "echarts";
import { construirColorPorAnio } from "../../../Helpers/ColoresPorAnio";
import { ordenarYLimitarPorAnio, MAX_FILAS_RANKING } from "../../../Helpers/RankingUtil";

// Gráfica de barras horizontales por año (Ventas por Familia/Cliente en
// Pantalla 1, Mezcla por Familia en Pantalla 3). Detalle completo en README.

// Colores del semáforo (texto enriquecido de echarts).
const COLOR_TEXTO = "#4f5a4b";
const COLOR_VERDE = "#367c2b";
const COLOR_AMARILLO = "#c9ac00";
const COLOR_ROJO = "#e24b4a";
const COLOR_MARGEN = "#1a4f8a"; // mismo azul ($blue) usado en otros módulos.

// Años a comparar (parámetro "comparativa" del endpoint real).
export const ANIOS = [];

// Alto de fila fija (px) y tope de filas visibles antes de hacer scroll.
const ALTO_FILA_FAMILIA = 100;
const FAMILIAS_VISIBLES = 12;

// Datos mock, solo si no se pasa `datos`.
export const datosVentasPorFamiliaMock = [
    { id: 1, etiqueta: "Lubricantes", valores: { 2023: 6890450, 2024: 7812300, 2025: 8654210, 2026: 9371625 } },
    { id: 2, etiqueta: "Repuestos John Deere", valores: { 2023: 3120800, 2024: 3654900, 2025: 3980210, 2026: 4254762 } },
    { id: 4, etiqueta: "Hule negro", valores: { 2023: 2410600, 2024: 2895400, 2025: 3210780, 2026: 3552312 } },
    { id: 3, etiqueta: "Filtros", valores: { 2023: 2650300, 2024: 2890100, 2025: 3105620, 2026: 3341827 } },
    { id: 5, etiqueta: "Baleros, Retenes, Bujes, Cojinetes y Rodamientos", valores: { 2023: 1180400, 2024: 1385200, 2025: 1548900, 2026: 1715503 } },
    { id: 6, etiqueta: "Discos agrícolas", valores: { 2023: 712300, 2024: 845600, 2025: 940100, 2026: 1047520 } },
    { id: 7, etiqueta: "Bombas", valores: { 2023: 610200, 2024: 712800, 2025: 815400, 2026: 926105 } },
    { id: 8, etiqueta: "Rafia forrajera", valores: { 2023: 520100, 2024: 615300, 2025: 712600, 2026: 803109 } },
    { id: 9, etiqueta: "Empaques, Sellos y Anillos", valores: { 2023: 410300, 2024: 490800, 2025: 565200, 2026: 645106 } },
    { id: 10, etiqueta: "Sistema de embrague", valores: { 2023: 260100, 2024: 315600, 2025: 370800, 2026: 426104 } },
    { id: 11, etiqueta: "Pernos, Tensores, Yugos, Estabilizadores, Chavetas, Pasadores, Candados y Horquillas", valores: { 2023: 165200, 2024: 198400, 2025: 232600, 2026: 268103 } },
    { id: 12, etiqueta: "Motor", valores: { 2023: 120300, 2024: 145600, 2025: 171200, 2026: 198105 } },
];

export const ChartFamiliasRefacciones = React.memo(({
    id = "chart-familias-refacciones",
    datos = datosVentasPorFamiliaMock,
    anios = ANIOS,
    anioPrincipal,
    orden = "desc",
    cantidad = "todos",
    metrica = "monto",
    seleccionados = [],
    onItemClick,
}) => {
    const chartRef = useRef(null);

    const anioClave = anioPrincipal || anios[anios.length - 1];
    const colorPorAnio = construirColorPorAnio(anios);

    // Ordenamiento/límite (Helpers/RankingUtil.js).
    const { ascendente, seleccionado, limitadoPorTope } = ordenarYLimitarPorAnio(datos, {
        anioClave,
        orden,
        cantidad,
    });

    // Alto real del canvas.
    const alturaChart = Math.max(seleccionado.length, 1) * ALTO_FILA_FAMILIA;

    // Número de renglón visual (1, 2, 3...), de arriba hacia abajo.
    const etiquetasConRango = seleccionado.map((item, i) => {
        const rango = orden === "asc" ? i + 1 : seleccionado.length - i;
        return `${rango}. ${item.etiqueta}`;
    });

    useEffect(() => {
        const dom = chartRef.current;
        if (!dom) {
            console.error("El contenedor de la gráfica no existe.");
            return;
        }

        if (echarts.getInstanceByDom(dom)) {
            echarts.dispose(dom);
        }

        const myChart = echarts.init(dom);

        const formatMoneda =
            metrica === "cantidad"
                ? (value) => `${Math.round(value).toLocaleString("es-MX")} unidades`
                : (value) => `$${Math.round(value).toLocaleString("es-MX")}`;

        const option = {
            tooltip: {
                trigger: "axis",
                axisPointer: { type: "shadow" },
                formatter: (params) => {
                    const etiqueta = params[0].name;
                    const filas = params
                        .map((p) => `${p.marker} ${p.seriesName}: ${formatMoneda(p.value)}`)
                        .join("<br/>");
                    return `<strong>${etiqueta}</strong><br/>${filas}`;
                },
            },
            // `right` fijo en px: espacio para el label de importe + % + flecha + margen.
            grid: {
                left: "1%",
                right: 290,
                top: "0%",
                bottom: "1%",
                containLabel: true,
            },
            animationDuration: 500,
            animationEasing: "cubicOut",
            xAxis: {
                type: "value",
                show: false,
            },
            yAxis: {
                type: "category",
                inverse: orden === "asc", // compensa el orden ascendente visualmente.
                data: etiquetasConRango,
                axisLine: { show: false },
                axisTick: { show: false },
                axisLabel: {
                    color: "#333",
                    fontSize: 11,
                    overflow: "truncate",
                    width: 160,
                },
            },
            series: anios.map((anio, indiceAnio) => ({
                name: anio,
                type: "bar",
                // Label por barra: importe + % vs. año anterior + flecha semáforo + margen.
                data: seleccionado.map((item) => {
                    const opacidad = seleccionados.length === 0 || seleccionados.includes(item.id) ? 1 : 0.3;
                    const valorNum = item.valores[anio] || 0;
                    let textoLabel = `{valor|${formatMoneda(valorNum)}}`;
                    if (indiceAnio > 0) {
                        const valorAnteriorNum = item.valores[anios[indiceAnio - 1]] || 0;
                        if (!valorAnteriorNum) {
                            textoLabel += `  {cambioGris|— sin dato}`;
                        } else {
                            // % que falta/sobra para llegar al 100% del año anterior
                            // (no el % directo): positivo = creció, negativo = cayó.
                            const cambioPorcentual = (valorNum / valorAnteriorNum) * 100 - 100;
                            // Amarillo queda definido pero sin usarse hasta que se
                            // defina el criterio (no borrar `cambioAmarillo`).
                            const rich =
                                cambioPorcentual > 0 ? "cambioVerde" : cambioPorcentual < 0 ? "cambioRojo" : "cambioGris";
                            const icono = cambioPorcentual > 0 ? "▲" : cambioPorcentual < 0 ? "▼" : "–";
                            const signo = cambioPorcentual > 0 ? "+" : "";
                            textoLabel += `  {${rich}|${signo}${cambioPorcentual.toFixed(1)}% ${icono}}`;
                        }
                    }
                    // Margen (importe - costo) / importe de ese año, después del %.
                    // Solo si `item.margenes` viene en `datos` (no todos los usos de
                    // este componente lo mandan; sin él, simplemente no se muestra).
                    const margenAnio = item.margenes?.[anio];
                    if (margenAnio !== undefined) {
                        textoLabel += `  {margen|Margen ${(margenAnio * 100).toFixed(1)}%}`;
                    }
                    return {
                        value: valorNum,
                        itemStyle: {
                            color: colorPorAnio[anio],
                            borderRadius: [0, 3, 3, 0],
                            opacity: opacidad,
                        },
                        label: { opacity: opacidad, formatter: textoLabel },
                    };
                }),
                label: {
                    show: true,
                    position: "right",
                    distance: 6,
                    rich: {
                        valor: { color: COLOR_TEXTO, fontWeight: 600, fontSize: 10 },
                        cambioVerde: { color: COLOR_VERDE, fontWeight: 700, fontSize: 10 },
                        cambioAmarillo: { color: COLOR_AMARILLO, fontWeight: 700, fontSize: 10 },
                        cambioRojo: { color: COLOR_ROJO, fontWeight: 700, fontSize: 10 },
                        cambioGris: { color: COLOR_TEXTO, fontWeight: 600, fontSize: 10 },
                        margen: { color: COLOR_MARGEN, fontWeight: 600, fontSize: 10 },
                    },
                },
                barMaxWidth: 20,
                barGap: "15%",
                barCategoryGap: "25px", // separación fija entre grupos.
            })),
        };

        myChart.setOption(option);

        // Drill-down: todo el renglón es clicable (no solo la barra). Se
        // ubica la fila por posición Y (convertFromPixel al eje Y de
        // categorías), no por el elemento exacto bajo el cursor, para que
        // el área gris/vacía de cada fila también cuente como clic.
        if (typeof onItemClick === "function") {
            const obtenerFilaPorPixel = (offsetY) => {
                const indice = Math.round(myChart.convertFromPixel({ yAxisIndex: 0 }, offsetY));
                return seleccionado[indice];
            };

            myChart.getZr().on("mousemove", (event) => {
                myChart.getZr().setCursorStyle(obtenerFilaPorPixel(event.offsetY) ? "pointer" : "default");
            });

            myChart.getZr().on("click", (event) => {
                const item = obtenerFilaPorPixel(event.offsetY);
                if (!item) return;
                onItemClick({ id: item.id, etiqueta: item.etiqueta });
            });
        }

        const handleResize = () => {
            myChart.resize();
        };
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
            myChart.dispose();
        };
    }, [datos, anios, anioClave, orden, cantidad, metrica, seleccionados, onItemClick]);

    return (
        <div className="cfr-wrapper">
            {limitadoPorTope && (
                <div className="cfr-aviso-limite">
                    Mostrando los {MAX_FILAS_RANKING} con mayor venta (de {ascendente.length} en total).
                </div>
            )}
            <div className="cfr-scroll scroll" style={{ maxHeight: `${FAMILIAS_VISIBLES * ALTO_FILA_FAMILIA}px` }}>
                <div id={id} ref={chartRef} style={{ width: "100%", height: `${alturaChart}px` }} />
            </div>
            <div className="cfr-legend">
                {anios.map((anio) => (
                    <span key={anio} className="cfr-legend-item">
                        <span
                            className="cfr-legend-dot"
                            style={{ backgroundColor: colorPorAnio[anio] }}
                        />
                        {anio}
                    </span>
                ))}
            </div>
        </div>
    );
});
